/**
 * MODULE: domain/pages/frontend/components/editor/PagesBookmarkBlockView.tsx
 * PURPOSE: Bookmark card and optional embed preview for external URLs
 */

import { NodeViewWrapper } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';
import { Button } from 'flowbite-react';
import { HiExternalLink, HiOutlinePhotograph } from 'react-icons/hi';
import { useIsPagesPublicView } from '../../context/PagesEditorViewModeContext';
import {
  bookmarkDisplayDomain,
  bookmarkFaviconUrl,
  bookmarkSupportsEmbed,
  buildBookmarkEmbedUrl,
  defaultBookmarkTitle,
  sanitizeBookmarkDisplay,
  shouldRenderBookmarkEmbed,
  type BookmarkBlockContent,
} from '../../utils/pageBookmarkUtils';

function readBookmarkContent(attrs: Record<string, unknown>): BookmarkBlockContent | null {
  const url = typeof attrs.url === 'string' ? attrs.url.trim() : '';
  if (!url) {
    return null;
  }
  return {
    url,
    title: typeof attrs.title === 'string' ? attrs.title : defaultBookmarkTitle(url),
    description: typeof attrs.description === 'string' ? attrs.description : null,
    image_url: typeof attrs.imageUrl === 'string' ? attrs.imageUrl : null,
    favicon_url: typeof attrs.faviconUrl === 'string' ? attrs.faviconUrl : bookmarkFaviconUrl(url),
    display: sanitizeBookmarkDisplay(
      typeof attrs.display === 'string' ? attrs.display : 'card',
      url
    ),
  };
}

export function PagesBookmarkBlockView({ node, selected, updateAttributes, editor }: NodeViewProps) {
  const isPublicView = useIsPagesPublicView();
  const editable = editor.isEditable && !isPublicView;
  const content = readBookmarkContent(node.attrs as Record<string, unknown>);

  if (!content) {
    return (
      <NodeViewWrapper className="my-3" data-testid="pages-bookmark-block">
        <div className="rounded-lg border border-dashed border-gray-300 px-4 py-3 text-sm text-gray-500 dark:border-gray-700">
          Invalid bookmark — add a URL to display this block.
        </div>
      </NodeViewWrapper>
    );
  }

  const domain = bookmarkDisplayDomain(content.url);
  const favicon = content.favicon_url ?? bookmarkFaviconUrl(content.url);
  const showEmbed = shouldRenderBookmarkEmbed(content, isPublicView);
  const embedUrl = showEmbed ? buildBookmarkEmbedUrl(content.url) : null;
  const canToggleEmbed = editable && bookmarkSupportsEmbed(content.url) && Boolean(buildBookmarkEmbedUrl(content.url));
  const displayMode = sanitizeBookmarkDisplay(content.display ?? 'card', content.url);

  const card = (
    <a
      href={content.url}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex w-full max-w-2xl items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 text-left transition-colors hover:border-blue-300 hover:bg-blue-50/60 dark:border-gray-700 dark:bg-gray-900/50 dark:hover:border-blue-700 dark:hover:bg-blue-950/20"
      data-testid="pages-bookmark-card"
    >
      <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md bg-white shadow-sm dark:bg-gray-950">
        {content.image_url ? (
          <img src={content.image_url} alt="" className="h-full w-full object-cover" />
        ) : (
          <img src={favicon} alt="" className="h-5 w-5" />
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span
          className="block truncate text-sm font-semibold text-gray-900 group-hover:text-blue-700 dark:text-white dark:group-hover:text-blue-300"
          data-testid="pages-bookmark-title"
        >
          {content.title}
        </span>
        {content.description ? (
          <span className="mt-0.5 block line-clamp-2 text-xs text-gray-600 dark:text-gray-400">
            {content.description}
          </span>
        ) : null}
        <span
          className="mt-1 block truncate text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400"
          data-testid="pages-bookmark-domain"
        >
          {domain}
        </span>
      </span>
      <HiExternalLink className="mt-1 h-4 w-4 shrink-0 text-gray-400 group-hover:text-blue-600" aria-hidden />
    </a>
  );

  return (
    <NodeViewWrapper
      className={`my-3 ${selected ? 'ring-2 ring-blue-300 ring-offset-2 dark:ring-blue-700 dark:ring-offset-gray-950' : ''}`}
      data-testid="pages-bookmark-block"
      data-display={isPublicView ? 'card' : displayMode}
    >
      {editable && canToggleEmbed ? (
        <div className="mb-2 flex flex-wrap gap-1.5" data-testid="pages-bookmark-display-toggle">
          <Button
            size="xs"
            color={displayMode === 'card' ? 'blue' : 'light'}
            onClick={() => updateAttributes({ display: 'card' })}
            data-testid="pages-bookmark-display-card"
          >
            Card
          </Button>
          <Button
            size="xs"
            color={displayMode === 'embed' ? 'blue' : 'light'}
            onClick={() => updateAttributes({ display: 'embed' })}
            data-testid="pages-bookmark-display-embed"
          >
            <HiOutlinePhotograph className="mr-1 h-3.5 w-3.5" aria-hidden />
            Embed
          </Button>
        </div>
      ) : null}

      {showEmbed && embedUrl ? (
        <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700" data-testid="pages-bookmark-embed">
          <div className="relative aspect-video w-full max-w-3xl bg-black">
            <iframe
              src={embedUrl}
              title={content.title}
              className="absolute inset-0 h-full w-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              loading="lazy"
              sandbox="allow-scripts allow-same-origin allow-presentation allow-popups"
            />
          </div>
          <div className="border-t border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-900/40">
            {card}
          </div>
        </div>
      ) : (
        card
      )}

      {isPublicView ? (
        <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400" data-testid="pages-bookmark-public-note">
          External link · opens in a new tab
        </p>
      ) : null}
    </NodeViewWrapper>
  );
}
